import { useEffect, useRef } from 'react';
import { deleteToken, getMessaging, getToken, isSupported, onMessage } from 'firebase/messaging';
import { arrayRemove, arrayUnion, doc, updateDoc } from 'firebase/firestore';
import { showMessage } from 'react-native-flash-message';
import { firebase, firestore, FIRESTORE_COLLECTIONS } from '../firebase';
import { WEB_PUSH_VAPID_KEY } from '../web/pushConfig';
import { navigateFromNotification, type NotificationTapData } from '../navigation/notificationNav';
import { translateCurrent } from '../i18n';

const PERMISSION_ASKED_KEY_PREFIX = '@tonyte/web-push-permission-requested/';
const LAST_TOKEN_KEY_PREFIX = '@tonyte/web-push-last-token/';

function getLastToken(uid: string): string | null {
  try {
    return localStorage.getItem(`${LAST_TOKEN_KEY_PREFIX}${uid}`);
  } catch {
    return null;
  }
}

function setLastToken(uid: string, token: string): void {
  try {
    localStorage.setItem(`${LAST_TOKEN_KEY_PREFIX}${uid}`, token);
  } catch {
    // Private-browsing/storage-disabled — a re-registration next load is
    // harmless (arrayUnion on an already-present token is a no-op).
  }
}

function clearLastToken(uid: string): void {
  try {
    localStorage.removeItem(`${LAST_TOKEN_KEY_PREFIX}${uid}`);
  } catch {
    // ignore
  }
}

/**
 * A just-registered service worker isn't necessarily active yet — it may
 * still be installing (or, on a repeat visit, waiting behind an already-
 * controlling older version). PushManager.subscribe() (inside FCM's
 * getToken()) requires an ACTIVE worker and rejects with an AbortError
 * otherwise. Resolves once this exact registration's worker reaches
 * 'activated', or immediately if it already has.
 */
function waitForServiceWorkerActive(registration: ServiceWorkerRegistration): Promise<void> {
  if (registration.active) return Promise.resolve();
  const worker = registration.installing || registration.waiting;
  if (!worker) return navigator.serviceWorker.ready.then(() => undefined);
  return new Promise<void>((resolve) => {
    const handleStateChange = () => {
      if (worker.state === 'activated') {
        worker.removeEventListener('statechange', handleStateChange);
        resolve();
      }
    };
    worker.addEventListener('statechange', handleStateChange);
  });
}

async function writeToken(uid: string, token: string, previousToken: string | null): Promise<void> {
  if (previousToken === token) return;
  const userRef = doc(firestore, FIRESTORE_COLLECTIONS.users, uid);
  // Same field/schema/multi-device pattern as usePushNotifications.ts
  // (native) — Cloud Functions' sendPushToUser() already fans out to
  // whatever's in this array regardless of platform, no backend change
  // needed to add a web token alongside existing iOS/Android ones.
  await updateDoc(userRef, { pushTokens: arrayUnion(token) });
  if (previousToken) {
    await updateDoc(userRef, { pushTokens: arrayRemove(previousToken) });
  }
  setLastToken(uid, token);
}

function toTapData(data: Record<string, string> | undefined): NotificationTapData {
  const d = data || {};
  return {
    type: d.type,
    chatId: d.chatId,
    chatKind: d.chatKind,
    messageId: d.messageId,
    senderId: d.senderId,
    senderName: d.senderName,
    planId: d.planId,
    requestId: d.requestId,
    requesterId: d.requesterId,
    requesterName: d.requesterName,
    otherUserId: d.otherUserId,
    isStudiZone: d.isStudiZone,
  };
}

/**
 * Web counterpart to usePushNotifications.ts. Registers
 * /firebase-messaging-sw.js (compiled by webpack.config.web.js as its own
 * entry), requests browser notification permission at most once per
 * (device, uid) — `Notification.permission` itself already remembers a
 * prior "denied" answer, so this never re-prompts — and writes the resulting
 * FCM token into the SAME `users/{uid}.pushTokens` array native uses.
 *
 * No-ops entirely (one console warning) if:
 *  - WEB_PUSH_VAPID_KEY hasn't been configured yet (src/web/pushConfig.ts), or
 *  - this browser/context doesn't support the Notifications/Messaging APIs
 *    at all (e.g. non-HTTPS origin, or Firebase Messaging's own
 *    isSupported() check failing).
 */
export function usePushNotifications(uid: string | null): void {
  const registeredForUidRef = useRef<string | null>(null);

  useEffect(() => {
    if (!uid || registeredForUidRef.current === uid) return undefined;
    registeredForUidRef.current = uid;
    let cancelled = false;
    let unsubscribeForeground: (() => void) | null = null;

    (async () => {
      try {
        if (!WEB_PUSH_VAPID_KEY) {
          console.warn('[Tonyte][Web][Push] WEB_PUSH_VAPID_KEY is not configured (src/web/pushConfig.ts) — Web Push stays disabled until it is.');
          return;
        }
        if (typeof window === 'undefined' || !('Notification' in window) || !('serviceWorker' in navigator)) {
          return;
        }
        if (!(await isSupported().catch(() => false))) {
          return;
        }

        if (Notification.permission === 'denied') return; // never re-prompt after denial
        let permission: NotificationPermission = Notification.permission;
        if (permission === 'default') {
          permission = await Notification.requestPermission();
        }
        if (cancelled || permission !== 'granted') return;

        const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
        if (cancelled) return;
        // A freshly-registered SW isn't necessarily active yet — reproduced
        // live: calling getToken() immediately after register() raced the
        // SW's install→activate lifecycle and PushManager.subscribe()
        // rejected with "no active Service Worker" (AbortError). Wait for
        // activation before ever touching PushManager, same as Firebase's
        // own guidance for a first-time registration.
        await waitForServiceWorkerActive(registration);
        if (cancelled) return;

        const messaging = getMessaging(firebase);
        const token = await getToken(messaging, {
          vapidKey: WEB_PUSH_VAPID_KEY,
          serviceWorkerRegistration: registration,
        });
        if (cancelled || !token) return;

        const previousToken = getLastToken(uid);
        await writeToken(uid, token, previousToken);

        // Foreground pushes: the tab is already focused, so a native OS
        // notification would be redundant — show the existing in-app toast
        // instead (same component background/foreground native's own
        // useNotificationListeners.ts foreground banner uses), tap-to-navigate
        // via the same shared router.
        unsubscribeForeground = onMessage(messaging, (payload) => {
          const tapData = toTapData(payload.data as Record<string, string> | undefined);
          showMessage({
            message: payload.notification?.title || translateCurrent('notifications.newVibeRequest', 'Tonyte'),
            description: payload.notification?.body,
            type: 'info',
            duration: 4000,
            backgroundColor: 'rgba(14,22,40,0.97)',
            color: '#fff',
            titleStyle: { fontWeight: '700', fontSize: 14 },
            textStyle: { color: 'rgba(255,255,255,0.72)', fontSize: 13 },
            onPress: () => navigateFromNotification(tapData),
          });
        });
      } catch (error) {
        // Never let a permission/token/SW-registration failure surface as an
        // unhandled rejection — a browser/session that can't do push just
        // doesn't get pushes.
        console.warn('[Tonyte][Web][Push] failed to register push token', error);
      }
    })();

    return () => {
      cancelled = true;
      unsubscribeForeground?.();
    };
  }, [uid]);
}

/** Removes this device's push token from `userId`'s doc — same contract as the native hook's export, called before sign-out completes. */
export async function removePushTokenForCurrentDevice(userId: string): Promise<void> {
  try {
    const token = getLastToken(userId);
    if (!token) return;
    await updateDoc(doc(firestore, FIRESTORE_COLLECTIONS.users, userId), {
      pushTokens: arrayRemove(token),
    });
    clearLastToken(userId);
    if (await isSupported().catch(() => false)) {
      await deleteToken(getMessaging(firebase)).catch(() => undefined);
    }
  } catch (error) {
    console.warn('[Tonyte][Web][Push] failed to remove push token on sign-out', error);
  }
}
